import { useState, Fragment, MouseEvent } from 'react';
import { styled, alpha, useTheme } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Menu, { MenuProps } from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const StyledMenu = styled((props: MenuProps) => (
    <Menu
        elevation={0}
        anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
        }}
        transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
        }}
        {...props}
    />
))(({ theme }) => ({
    '& .MuiPaper-root': {
        borderRadius: 6,
        color:
            theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
        boxShadow:
            'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
        '& .MuiMenu-list': {
            padding: '2px 0',
        },
        '& .MuiMenuItem-root': {
            '& .MuiSvgIcon-root': {
                color: theme.palette.text.secondary,
                marginRight: theme.spacing(0.5),
            },
            '&:active': {
                backgroundColor: alpha(
                    theme.palette.primary.main,
                    theme.palette.action.selectedOpacity,
                ),
            },
        },
    },
}));

interface DropDownProps {
    data: DropDownMenu[];
    color: string;
    backgroundColor: string;
    fontSize: number;
    defaultLabel: string;
    boxShadow: boolean;
    width: string;
    border: boolean;
    fontWeight: string;
    handler?: (event: MouseEvent<HTMLElement>, val: string) => void;
}

export interface DropDownMenu {
    label: string;
    value: string;
}

DropDownButton.defaultProps = {
    data: [{ label: 'PDF', value: 'pdf' }],
    backgroundColor: '#FFFFFF',
    fontSize: 13,
    defaultLabel: 'Select',
    boxShadow: true,
    width: '120px',
    border: true,
    color: '',
    fontWeight: 'normal'
}

export const defaultMenus: DropDownMenu[] = [ 
    { label: 'Week', value: 'week' }, 
    { label: 'Month', value: 'month' },
    { label: 'Year', value: 'year' }
];

export default function DropDownButton({ data, color, backgroundColor, fontSize, defaultLabel, boxShadow, width, border, handler, fontWeight}: DropDownProps) {
    
    const theme = useTheme();

    const theColor = color !== '' ? color : theme.palette.secondary.main;
    
    const [selected, setSelected] = useState(defaultLabel);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const handleSelect = (event: MouseEvent<HTMLElement>, index: number) => {
        setAnchorEl(null);
        setSelected(data[index].label);
        if (handler) {
            handler(event, data[index].value);
        }
    };


    return (
        <Fragment>
            <Button
                sx={{
                    background:'white 0% 0% no-repeat padding-box',
                    boxShadow: boxShadow ? '0px 0px 8px ' + theme.palette.grey.A200 : 'none',
                    borderRadius: border ? 2 : 0,
                    justifyContent: 'space-between',
                    backgroundColor: border ? backgroundColor: 'transparent',
                    color: theColor,
                    fontSize: {
                        xs: fontSize-2,
                        md: fontSize-1,
                        xl: fontSize
                    },
                    padding: '4px 12px 4px 12px',
                    minWidth: width,
                    fontWeight
                }}
                size="large"
                color="primary"
                id="drop-down-button"
                aria-controls="drop-down-menu"
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                disableElevation={true}
                onClick={handleClick}
                endIcon={boxShadow && border ? <KeyboardArrowDownIcon color="secondary" /> : <KeyboardArrowDownIcon sx={{ borderRadius: 1, backgroundColor: border ? 'transparent': 'grey.500'}} />}
            >
                {selected}
            </Button>
            <StyledMenu
                id="drop-down-menu"
                MenuListProps={{
                    'aria-labelledby': 'drop-down-button',
                }}
                sx={{
                    fontWeight
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
            >
                {data.map((entry, index) => {
                    return <MenuItem key={index} onClick={(event) => handleSelect(event, index)} disableRipple={true} 
                        sx={{ justifyContent: 'center', fontWeight, fontSize, color: theColor, minWidth: width, borderWidth: 5, borderColor: 'white', borderStyle: 'solid', borderRadius: 2 }}>
                        {entry.label}
                    </MenuItem>
                })}
            </StyledMenu>
        </Fragment>
    );
}
